import { useMemo, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from "recharts";
import styles from "../../styles/portfolioManagerDashboard.module.scss";
import KpiInfoIcon from "./KpiInfoIcon";
import { HealthKpiStatus } from "../../../../helpers/utils";
import { getAccountCurrency } from "../../../../utils/systemSettings";
import { formatCompactMoney } from "./kpiHelpers";

const statusColor = (status) => HealthKpiStatus.find((s) => s.value === status)?.color ?? HealthKpiStatus[0].color;

const settings = JSON.parse(localStorage.getItem("settings"));
const currency = getAccountCurrency(settings);
const formatMoney = (val) => formatCompactMoney(settings, currency, val);
const formatPercent = (val) => `${val}%`;
const formatCount = (val) => `${val}`;

// Every metric a group card shows, made comparable across groups in one ranked chart - the user
// picks which KPI to rank by instead of us guessing which one matters right now.
const METRICS = [
  { key: "budget", label: "Budget", getValue: (r) => r.costsSummary?.budget ?? 0, format: formatMoney, info: "Total budget summed across all projects in the group." },
  { key: "grossMargin", label: "Gross Margin", getValue: (r) => r.costsSummary?.grossMarginPercentage ?? 0, format: formatPercent, info: "(Invoiced − Actual cost) ÷ Invoiced, as a percentage." },
  { key: "progress", label: "Progress", getValue: (r) => r.overallProgress ?? 0, format: formatPercent, info: "Average completion percentage across projects in the group." },
  { key: "onTime", label: "On-Time Delivery", getValue: (r) => Math.round((r.onTimeDelivery ?? 0) * 100), format: formatPercent, info: "Share of completed projects that finished on time." },
  { key: "activeProjects", label: "Active Projects", getValue: (r) => r.activeProjects ?? 0, format: formatCount, info: "Projects not yet Completed or On Hold." },
  { key: "atRisk", label: "At Risk", getValue: (r) => r.projectsAtRiskCurrent ?? 0, format: formatCount, info: "Projects whose forecasted health next period is worse than their current actual health." },
  { key: "risks", label: "Open Risks", getValue: (r) => (r.risksBySeverity || []).reduce((sum, s) => sum + s.count, 0), format: formatCount, info: "Total open risks across all severities." },
  { key: "valueDelivered", label: "Value Delivered", getValue: (r) => (r.valueDelivered?.length > 0 ? r.valueDelivered[r.valueDelivered.length - 1].actual : 0), format: formatMoney, info: "Cumulative actual value delivered so far (only computed for the top groups by project count)." },
];

function CustomTooltip({ active, payload, metric }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className={styles.pvTooltip}>
      <span className={styles.pvTooltipLabel}>{row.name}</span>
      <span className={styles.pvTooltipValue}>{metric.format(row.value)}</span>
    </div>
  );
}

const isSameGroup = (a, b) => a && b && a.groupType === b.groupType && a.id === b.id;

function GroupComparisonChart({ groups = [], selectedGroup, onSelectGroup }) {
  const [metricKey, setMetricKey] = useState("budget");
  const metric = METRICS.find((m) => m.key === metricKey) ?? METRICS[0];

  const data = useMemo(
    () =>
      [...groups]
        .sort((a, b) => metric.getValue(b) - metric.getValue(a))
        .slice(0, 8)
        .map((row) => ({
          key: `${row.groupType}-${row.group?.id ?? "none"}`,
          name: row.group?.name ?? "All",
          value: metric.getValue(row),
          color: statusColor(row.health?.current ?? 0),
          groupType: row.groupType,
          id: row.group?.id,
        })),
    [groups, metric]
  );

  if (!data.length) return null;

  return (
    <div className={styles.analyticsPanel}>
      <div className={styles.panelHeader}>
        <div className="d-flex align-items-center gap-2">
          <span className={styles.label}>{metric.label} by Group</span>
          <KpiInfoIcon content={metric.info} inline />
        </div>
        <select
          className={styles.metricSelect}
          value={metricKey}
          onChange={(e) => setMetricKey(e.target.value)}
        >
          {METRICS.map((m) => (
            <option key={m.key} value={m.key}>
              {m.label}
            </option>
          ))}
        </select>
      </div>
      <div style={{ width: "100%", height: Math.max(180, data.length * 40 + 40) }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
            <XAxis
              type="number"
              tick={{ fontSize: 10, fill: "#9e9e9e" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={metric.format}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={120}
              tick={{ fontSize: 11, fill: "#424242" }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip metric={metric} />} cursor={{ fill: "#f5f5f5" }} />
            <Bar
              dataKey="value"
              radius={[0, 4, 4, 0]}
              barSize={18}
              cursor="pointer"
              onClick={(entry) => {
                const next = { groupType: entry.groupType, id: entry.id, name: entry.name };
                onSelectGroup?.(isSameGroup(selectedGroup, next) ? null : next);
              }}
            >
              {data.map((entry) => (
                <Cell
                  key={entry.key}
                  fill={entry.color}
                  fillOpacity={selectedGroup && !isSameGroup(selectedGroup, entry) ? 0.4 : 1}
                />
              ))}
              <LabelList dataKey="value" position="center" formatter={metric.format} style={{ fontSize: 11, fontWeight: 600, fill: "#fff" }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default GroupComparisonChart;
